/* eslint-disable import/no-extraneous-dependencies */
import React, { useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Button } from '@chakra-ui/react';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import { useAuth } from '../../../providers/auth';
import { InputForm } from '../../inputs/forms/InputForm';
import { FormButton } from '../../buttons/FormButton';

const schema = yup.object().shape({
  email: yup.string().email('E-mail inválido').required('E-mail obrigatório'),
  password: yup.string().required('Senha ou token obrigatório'),
});

interface IForm {
  email: string;
  password: string;
}

// Em desenvolvimento desabilitamos totalmente o captcha
const isCaptchaDisabled =
  process.env.NODE_ENV === 'development' || process.env.REACT_APP_DISABLE_CAPTCHA === 'true';

// Função para detectar se o valor é um token (formato: XXXX-XXXX-XXXX)
const isTokenFormat = (value: string): boolean => {
  const tokenPattern = /^[A-Z0-9]{4}-[A-Z0-9]{4}-[A-Z0-9]{4}$/i;
  return tokenPattern.test(value.trim());
};

export default function LoginForm() {
  const { Login, LoginDelegado } = useAuth();
  const navigate = useNavigate();
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const captchaRef = useRef<any>(null);

  const formMethods = useForm<IForm>({
    resolver: yupResolver(schema),
    defaultValues: {
      email: process.env.NODE_ENV === 'development' ? 'sys_sensi@mailinator.com' : '',
      password: process.env.NODE_ENV === 'development' ? '8QW2uuIP' : '',
    },
  });

  const onSubmit = async (values: IForm) => {
    // Verifica captcha para ambos os casos
    if (!isCaptchaDisabled && !captchaToken) {
      toast.error('Por favor, complete o captcha antes de enviar.', { position: 'top-center' });
      return;
    }

    const passwordOrToken = values.password.trim();
    const isToken = isTokenFormat(passwordOrToken);

    // Sempre tenta login normal primeiro
    try {
      await Login({ ...values, captchaToken: captchaToken || 'disabled' });
      toast.success('Login realizado com sucesso!', { position: 'top-center' });
      setCaptchaToken(null);
      captchaRef.current?.resetCaptcha();
    } catch (error: any) {
      // Se login normal falhou, verifica se é formato de token
      if (isToken) {
        // Se for formato de token, tenta login delegado
        try {
          await LoginDelegado(values.email, passwordOrToken.toUpperCase());
          toast.success('Login com acesso delegado realizado com sucesso!', {
            position: 'top-center',
          });
          setCaptchaToken(null);
          captchaRef.current?.resetCaptcha();
        } catch (tokenError: any) {
          const tokenErrorMessage =
            tokenError?.response?.data?.detail ?? 'Token de acesso inválido ou expirado.';
          toast.error(tokenErrorMessage, { position: 'top-center' });
          setCaptchaToken(null);
          captchaRef.current?.resetCaptcha();
        }
      } else {
        // Se não for token e login normal falhou, mostra erro de login normal
        const errorMessage =
          error?.response?.data?.detail ?? 'Erro interno no sistema, Tente novamente mais tarde.';
        toast.error(errorMessage, { position: 'top-center' });
        setCaptchaToken(null);
        captchaRef.current?.resetCaptcha();
      }
    }
  };

  const handleCaptchaVerification = (token: string) => {
    setCaptchaToken(token);
  };

  const handleCaptchaError = () => {
    toast.error('Erro ao verificar o captcha. Por favor, tente novamente.', {
      position: 'top-center',
    });
  };

  return (
    <div className="w-full">
      <form onSubmit={formMethods.handleSubmit(onSubmit)}>
        <FormProvider {...formMethods}>
          <InputForm name="email" label="E-mail" placeholder="Seu e-mail de login" />
          <InputForm name="password" type="password" label="Senha" placeholder="Senha" />

          {!isCaptchaDisabled && (
            <HCaptcha
              sitekey={process.env.REACT_APP_HCAPTCHA_SITEKEY ?? ''}
              onVerify={handleCaptchaVerification}
              onError={handleCaptchaError}
              ref={captchaRef}
            />
          )}

          <div className="flex justify-end">
            <Button variant="link" color="text.tertiary" onClick={() => navigate('/recover')}>
              Esqueci a senha?
            </Button>
          </div>

          <FormButton mt={4} isLoading={formMethods.formState.isSubmitting} type="submit" w="100%">
            Entrar
          </FormButton>
        </FormProvider>
      </form>
    </div>
  );
}
